import { oc } from "@orpc/contract";
import { z } from "zod";

const RebateCsvTransformSessionStatusSchema = z.enum([
  "pending_sheet_selection",
  "queued",
  "running",
  "waiting_manual",
  "ready_for_review",
  "completed",
  "failed",
  "cancelled",
]);

export const CreateRebateCsvTransformSessionRequestSchema = z.object({
  fileName: z.string().describe("Original file name of the source file"),
  contentType: z
    .string()
    .default("application/octet-stream")
    .describe("MIME type of the file"),
  fileBase64: z
    .string()
    .describe("Base64-encoded file content to upload to blob storage"),
  fileNames: z
    .array(z.string())
    .optional()
    .describe("All file names if multiple were selected"),
  sourceContext: z.record(z.string(), z.unknown()).optional(),
  selectedSheet: z
    .string()
    .optional()
    .describe(
      "Sheet name to use for XLSX files. If omitted for multi-sheet XLSX, session enters pending_sheet_selection status.",
    ),
});

export const SelectRebateCsvTransformSheetRequestSchema = z.object({
  sessionId: z.string().uuid(),
  sheetName: z.string().describe("Name of the XLSX sheet to use as input"),
});

export const SaveRebateCsvTransformSnapshotRequestSchema = z.object({
  sessionId: z.string().uuid(),
  stepLabel: z
    .string()
    .describe(
      "Step label matching workflow step: preprocess, map_fields, transform, finalize",
    ),
  csvContent: z
    .string()
    .describe("CSV string output of this phase to persist to blob storage"),
  state: z
    .record(z.string(), z.unknown())
    .optional()
    .describe("Phase-specific state (constants, mappings, explanations, etc.)"),
  rowCountOutput: z.number().int().optional(),
});

export const SaveRebateCsvTransformSnapshotResponseSchema = z.object({
  snapshotId: z.string().uuid(),
  outputArtifactKey: z.string(),
});

export const StartRebateCsvTransformRequestSchema = z.object({
  sessionId: z
    .string()
    .uuid()
    .optional()
    .describe("Existing session to resume from (skips completed steps)"),
  inputArtifactKey: z
    .string()
    .optional()
    .describe("Blob key of uploaded CSV (within default container)"),
  documentId: z
    .string()
    .uuid()
    .optional()
    .describe(
      "Document ID from upload; used to resolve inputArtifactKey if provided instead of inputArtifactKey",
    ),
  instructions: z.string().optional(),
  fileNames: z.array(z.string()).optional(),
  sourceContext: z.record(z.string(), z.unknown()).optional(),
});

export const RebateCsvTransformSessionResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  createdBy: z.string().uuid().nullable(),
  workflowDefinitionId: z.string().uuid().nullable(),
  workflowRunId: z.string().uuid().nullable(),
  sourceContext: z.record(z.string(), z.unknown()).nullable(),
  status: RebateCsvTransformSessionStatusSchema,
  autoRunRemainder: z.boolean(),
  currentStepId: z.string().uuid().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const CreateRebateCsvTransformSessionResponseSchema =
  RebateCsvTransformSessionResponseSchema;

export const GetRebateCsvTransformSessionRequestSchema = z.object({
  sessionId: z.string().uuid(),
});

export const ListRebateCsvTransformSessionsRequestSchema = z.object({
  limit: z.number().int().min(1).max(100).optional(),
  fromDate: z.string().datetime().optional(),
  toDate: z.string().datetime().optional(),
});

export const ListRebateCsvTransformSnapshotsRequestSchema = z.object({
  sessionId: z.string().uuid(),
});

export const RebateCsvTransformSnapshotResponseSchema = z.object({
  id: z.string().uuid(),
  sessionId: z.string().uuid(),
  stepLabel: z.string(),
  workflowRunId: z.string().uuid().nullable(),
  stepRunId: z.string().uuid().nullable(),
  attemptNumber: z.number(),
  state: z.record(z.string(), z.unknown()).nullable(),
  inputArtifactKey: z.string().nullable(),
  outputArtifactKey: z.string().nullable(),
  rowCountInput: z.number().nullable(),
  rowCountOutput: z.number().nullable(),
  errorMessage: z.string().nullable(),
  failed: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const GetRebateCsvTransformArtifactRequestSchema = z.object({
  sessionId: z.string().uuid(),
  artifactKey: z.string(),
});

export const GetRebateCsvTransformArtifactResponseSchema = z.object({
  csvContent: z.string(),
});

export const RewindRebateCsvTransformStepRequestSchema = z.object({
  sessionId: z.string().uuid(),
  stepLabel: z.string(),
  inputOverride: z.record(z.string(), z.unknown()).optional(),
});

export const ResumeAutoRunRemainderRequestSchema = z.object({
  sessionId: z.string().uuid(),
});

export const CompleteRebateCsvTransformSessionRequestSchema = z.object({
  sessionId: z.string().uuid(),
});

export const CheckDuplicateRebateCsvTransformSessionsRequestSchema = z.object({
  fileName: z
    .string()
    .describe("File name to check for existing active sessions"),
});

export const CheckDuplicateRebateCsvTransformSessionsResponseSchema = z.object({
  duplicates: z.array(
    z.object({
      id: z.string().uuid(),
      status: RebateCsvTransformSessionStatusSchema,
      createdAt: z.date(),
      createdByName: z.string().nullable(),
      fileNames: z.array(z.string()),
    }),
  ),
});

export const DeleteRebateCsvTransformSessionRequestSchema = z.object({
  sessionId: z.string().uuid(),
});

export const GetRebateSenderContactOfferRequestSchema = z.object({
  sessionId: z.string().uuid(),
  supplierId: z.string().uuid(),
});

export const RebateSenderContactOfferSchema = z.object({
  senderEmail: z.string(),
  supplierName: z.string(),
  senderDomain: z.string().nullable(),
});

export const GetRebateSenderContactOfferResponseSchema = z.object({
  offer: RebateSenderContactOfferSchema.nullable(),
});

export const SaveRebateSenderContactRequestSchema = z.object({
  sessionId: z.string().uuid(),
  supplierId: z.string().uuid(),
});

export const SaveRebateSenderContactResponseSchema = z.object({
  created: z.boolean(),
  contactId: z.string().uuid().nullable(),
});

export const rebateCsvTransformContracts = {
  createSession: oc
    .input(CreateRebateCsvTransformSessionRequestSchema)
    .output(CreateRebateCsvTransformSessionResponseSchema)
    .route({
      summary: "Upload a rebate file for conversion",
      description:
        "Uploads a rebate data file (CSV or spreadsheet) and creates a conversion session without starting it. Multi-sheet spreadsheets wait for a sheet choice.",
    }),

  saveSnapshot: oc
    .input(SaveRebateCsvTransformSnapshotRequestSchema)
    .output(SaveRebateCsvTransformSnapshotResponseSchema)
    .route({
      summary: "Save a rebate conversion step result",
      description:
        "Saves the CSV output and state for one conversion step (preprocess, map fields, transform, finalize) as a new attempt, discarding later steps' results.",
    }),

  start: oc
    .input(StartRebateCsvTransformRequestSchema)
    .output(RebateCsvTransformSessionResponseSchema)
    .route({
      summary: "Start converting a rebate file",
      description:
        "Starts the AI conversion workflow that turns an uploaded rebate file into the standard format, for a new upload or resuming an existing session.",
    }),

  getSession: oc
    .input(GetRebateCsvTransformSessionRequestSchema)
    .output(RebateCsvTransformSessionResponseSchema.nullable())
    .route({
      summary: "Get a rebate conversion session",
      description:
        "Returns a rebate file conversion session's status, current step, workflow run, and source details, or null if not found.",
    }),

  listSessions: oc
    .input(ListRebateCsvTransformSessionsRequestSchema.optional())
    .output(z.array(RebateCsvTransformSessionResponseSchema))
    .route({
      summary: "List rebate conversion sessions",
      description:
        "Lists the organization's rebate file conversion sessions, optionally limited by count and creation date range.",
    }),

  listSnapshots: oc
    .input(ListRebateCsvTransformSnapshotsRequestSchema)
    .output(z.array(RebateCsvTransformSnapshotResponseSchema))
    .route({
      summary: "List a rebate conversion's step results",
      description:
        "Lists saved step results for a rebate conversion session, with attempt number, row counts, state, output file, and errors.",
    }),

  getArtifact: oc
    .input(GetRebateCsvTransformArtifactRequestSchema)
    .output(GetRebateCsvTransformArtifactResponseSchema)
    .route({
      summary: "Get a rebate conversion CSV",
      description:
        "Returns the CSV content of a rebate conversion session's source file or a step's output file.",
    }),

  rewindStep: oc
    .input(RewindRebateCsvTransformStepRequestSchema)
    .output(z.object({ success: z.boolean() }))
    .route({
      summary: "Re-run a rebate conversion from a step",
      description:
        "Re-runs a rebate file conversion from the chosen step, optionally with edited step input, restarting the workflow from there.",
    }),

  resumeAutoRun: oc
    .input(ResumeAutoRunRemainderRequestSchema)
    .output(z.object({ success: z.boolean() }))
    .route({
      summary: "Let a rebate conversion finish automatically",
      description:
        "Switches a rebate conversion session to run its remaining steps automatically without manual review.",
    }),

  completeSession: oc
    .input(CompleteRebateCsvTransformSessionRequestSchema)
    .output(z.object({ success: z.boolean() }))
    .route({
      summary: "Mark a rebate conversion as completed",
      description: "Marks a rebate file conversion session as completed.",
    }),

  selectSheet: oc
    .input(SelectRebateCsvTransformSheetRequestSchema)
    .output(RebateCsvTransformSessionResponseSchema)
    .route({
      summary: "Choose the sheet for a rebate conversion",
      description:
        "Picks which sheet of an uploaded multi-sheet spreadsheet a rebate conversion session uses, converting it to CSV so the session can start.",
    }),

  checkDuplicateSessions: oc
    .input(CheckDuplicateRebateCsvTransformSessionsRequestSchema)
    .output(CheckDuplicateRebateCsvTransformSessionsResponseSchema)
    .route({
      summary: "Check for existing rebate conversions of a file",
      description:
        "Finds active rebate conversion sessions already working on the same file name, with status, creator, and creation time.",
    }),

  deleteSession: oc
    .input(DeleteRebateCsvTransformSessionRequestSchema)
    .output(z.object({ success: z.boolean() }))
    .route({
      summary: "Delete a rebate conversion session",
      description:
        "Deletes a rebate file conversion session and its saved step results.",
    }),

  getSenderContactOffer: oc
    .input(GetRebateSenderContactOfferRequestSchema)
    .output(GetRebateSenderContactOfferResponseSchema)
    .route({
      summary: "Check whether to offer saving a rebate email sender",
      description:
        "Returns the unmatched inbound email sender and chosen supplier when the user should be offered to save that address as a contact, or null if no offer should be shown.",
    }),

  saveSenderContact: oc
    .input(SaveRebateSenderContactRequestSchema)
    .output(SaveRebateSenderContactResponseSchema)
    .route({
      summary: "Save a rebate email sender as a supplier contact",
      description:
        "Creates a contact for the inbound email sender on the chosen supplier's company when the offer conditions still hold, or returns the existing contact without creating a duplicate.",
    }),
};
